import { PointOptionsObject } from 'highcharts/highcharts';
import cloneDeep from 'lodash.clonedeep';

import { WidgetType } from '../../../types/widget.types';
import { DEFAULT_TEXT_STYLE, chartColorPalette } from '../ChartWidget.config';
import { CustomLegendRender, applyPatterns, extendSeriesData } from '../ChartWidget.helpers';
import { HighchartsSeriesData } from '../ChartWidget.hooks';
import { ChartDataSeries, ChartWidgetData } from '../ChartWidget.types';
import { HighchartsOptions } from '../DataTableChartCommon/DataTableChartCommon.types';
import { DEFAULT_BORDER_COLOR, DEFAULT_BORDER_WIDTH, DEFAULT_HEIGHT, DEFAULT_WIDTH } from './TreeMapChartWidget.config';
import { TreeMapChartWidgetData } from './TreeMapChartWidget.types';
import { VERSION } from './TreeMapChartWidget.upgrade';

export const generateDefaultData = () => {
  return {
    widgetType: WidgetType.TreeMapChart,
    widgetData: {
      version: VERSION,
      widthPx: DEFAULT_WIDTH,
      heightPx: DEFAULT_HEIGHT,
      topPx: 0,
      leftPx: 0,
      rotateDeg: 0,
      altText: '',
      isDecorative: false,
      legend: {
        enabled: true,
        labelStyle: {
          ...DEFAULT_TEXT_STYLE,
        },
      },
      y2DataLabels: {
        enabled: true,
        style: {
          ...DEFAULT_TEXT_STYLE,
          color: '#fff',
        },
      },
      dataLabels: {
        enabled: true,
        style: {
          ...DEFAULT_TEXT_STYLE,
          color: '#fff',
        },
      },
      seriesData: extendSeriesData([
        {
          data: ['Q1', 'Q2', 'Q3'],
        },
        {
          name: 'Marketing',
          data: [63, 63, 42],
        },
        {
          name: 'IT',
          data: [42, 50, 40],
        },
        {
          name: 'Operations',
          data: [30, 38, 29],
        },
      ] as ChartDataSeries),
      seriesMeta: {
        colors: chartColorPalette,
      },
      xAxis: {
        title: {
          text: 'Time',
          style: {
            ...DEFAULT_TEXT_STYLE,
            fontWeight: 'bold',
          },
        },
        style: {
          lineColor: '#000000',
          lineWidth: 1,
        },
        labelStyle: {
          ...DEFAULT_TEXT_STYLE,
        },
      },
      yAxis: {
        title: {
          text: 'Percentage usage',
          style: {
            ...DEFAULT_TEXT_STYLE,
            fontWeight: 'bold',
          },
        },
        enableLine: false,
        style: {
          lineColor: '#000000',
          lineWidth: 1,
        },
        labelStyle: {
          ...DEFAULT_TEXT_STYLE,
        },
      },
      grid: {
        style: {
          lineColor: '#e6e6e6',
        },
      },
      generalOptions: {
        innerSize: 0,
        borderWidth: 2,
        borderColor: '#FFFFFF',
      },
      patterns: {
        enabled: false,
        list: [],
      },
    } as TreeMapChartWidgetData,
  };
};

interface TreeMapSeriesParent {
  id: string;
  name: string;
  color: string;
}

interface TreeMapSeriesChild {
  name: string;
  parent: string;
  value: number;
}

type TreeMapData = (TreeMapSeriesParent | TreeMapSeriesChild)[];

/**
 * Transforms Highcharts series and header data into TreeMapData.
 *
 * The provided series data is expected to have a name that matches the id of
 * one of the parent objects in the header. The data is then added to the output
 * array as children of the parent object.
 *
 * @param {HighchartsSeriesData[]} series The Highcharts series data
 * @param {{[key: string]: TreeMapSeriesParent}} header The header data
 * @param {string[]} categories The categories of the data
 * @returns {TreeMapData} The transformed data
 */
const transformTreeMapData = (
  series: HighchartsSeriesData[],
  header: { [key: string]: TreeMapSeriesParent },
  categories: Array<string>,
): TreeMapData => {
  // Initialize the output array with parent
  const outputArray: TreeMapData = Object.values(header);

  // Add children for each parent
  series.forEach((child) => {
    const parent = header[child?.name as string];
    if (parent) {
      (child?.data as PointOptionsObject[])?.forEach((value, index) => {
        outputArray.push({
          name: `${categories[index]}`,
          parent: parent.id,
          value: value.y as number,
        });
      });
    }
  });

  return outputArray;
};

export const getHighChartsTreeMapConfig = (
  defaultOptions: HighchartsOptions,
  widgetData: ChartWidgetData,
  categories: Array<string>,
  series: HighchartsSeriesData[],
  keyCustom: number,
) => {
  const options = defaultOptions;

  const {
    y2DataLabels,
    dataLabels: { enabled: enableDataLevelOneLabel, style: dataLabelStyle },
    generalOptions: {
      borderWidth = DEFAULT_BORDER_WIDTH,
      borderColor = DEFAULT_BORDER_COLOR,
      borderRadius,
      layoutAlgorithm = 'squarified',
    } = {},
    legend: { enabled: enableLegend },
  } = widgetData;

  const { enabled: patternsEnabled, list: patternsList } = widgetData.patterns;

  options.chart = options.chart || {};
  options.chart.type = 'treemap';
  options.chart.events = { render: CustomLegendRender };

  options.plotOptions = options.plotOptions || {};
  options.plotOptions.treemap = options.plotOptions.treemap || {};

  options.plotOptions.treemap = {
    animation: false,
    layoutAlgorithm,
    alternateStartingDirection: true,
    showInLegend: enableLegend,
    borderRadius: borderRadius,
    borderWidth,
    borderColor,
    dataLabels: {
      enabled: y2DataLabels?.enabled,
      style: {
        ...DEFAULT_TEXT_STYLE,
        ...y2DataLabels?.style,
      },
    },
    levels: [
      {
        level: 1,
        dataLabels: {
          enabled: enableDataLevelOneLabel,
          align: 'left',
          verticalAlign: 'top',
          style: {
            ...DEFAULT_TEXT_STYLE,
            color: dataLabelStyle.color,
            fontSize: dataLabelStyle.fontSize,
            fontFamily: dataLabelStyle.fontFamily,
            fontWeight: dataLabelStyle.fontWeight,
            fontStyle: dataLabelStyle.fontStyle,
            textDecoration: dataLabelStyle.textDecoration,
          },
        },
      },
    ],
  };
  options.keyCustom = keyCustom;

  options.plotOptions.series = {
    enableMouseTracking: false,
    shadow: false,
  };

  let level1Header: { [key: string]: TreeMapSeriesParent } = {};
  series.forEach(({ name, color }, idx) => {
    const id = `${name}-${idx}`;
    level1Header = {
      ...level1Header,
      [id]: {
        id,
        name,
        color,
      } as TreeMapSeriesParent,
    };
  });

  const data = transformTreeMapData(
    series.map(({ name, color, data }, idx) => {
      return {
        name: `${name}-${idx}`, // in case we have same name
        color,
        data: cloneDeep(data),
        state: { hover: { enabled: false } },
        type: 'treemap',
      };
    }),
    level1Header,
    categories,
  );

  options.series = series.map(({ name, color }) => {
    return {
      name,
      color,
      data: cloneDeep(data),
      state: { hover: { enabled: true } },
      type: 'treemap',
    };
  });

  if (patternsEnabled) {
    applyPatterns(options, patternsList);
  }

  return options;
};
